// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Box } from 'grommet'
import { User, Trash } from 'grommet-icons'
import { isEmpty } from 'lodash'

import { useVisibilityContext } from '../../../../context/visibility-context'
import {
  ActionButton,
  Anchor,
  Typography,
  Button,
  DataTable,
  NoDataInfo
} from '../../../../components'
import {
  get,
  getBaseUrl,
  getErrorMessage,
  patch
} from '../../../../utils/api-utils'
import AssignUsersToGroupModal from '../../commoncomponents/AssignUsersToGroupModal'
import RemoveUserFromGroupConfirmation from '../../commoncomponents/RemoveUserFromGroupConfirmation'
import { USER_SCHEMAS } from '../../users/constants'
import { GROUP_SCHEMAS } from '../constants'
import { getPaginationShowIdx } from '../../../../utils/common-utils'
import { updateV2RBACPolicies } from '../../../../utils/rbac-api-utils'
import { useCCSContext } from '../../../../context/ccs-context'

const GroupDetailsUsers = ({
  isScim,
  groupDetailsData,
  setNotificationInfo,
  parentLoading,
  isWorkspace = false,
  isAssociated
}) => {
  const { csrfToken } = useCCSContext()
  const { dispatchVisibilityContext } = useVisibilityContext()
  const { t } = useTranslation(['iam', 'common', 'auditlogs'])
  const navigate = useNavigate()
  const { groupId } = useParams()
  const [groupUsers, setGroupUsers] = useState()
  const [loading, setLoading] = useState(true)
  const [refreshCount, setRefreshCount] = useState(0)
  const [selectedUser, setSelectedUser] = useState()
  const [showAddUsersDialog, setShowAddUsersDialog] = useState(false)
  const [showDeleteUsersDialog, setShowDeleteUsersDialog] = useState(false)
  const { v2RbacPolicies, v2RbacRootWkspcPolicies } = useVisibilityContext()
  const v2GroupUpdatePerm = v2RbacPolicies?.includes(
    'identity.user-group-members.update'
  )
  const rootWkspGroupUpdatePerm = v2RbacRootWkspcPolicies?.includes(
    'identity.user-group-members.update'
  )
  // for pagination
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(1)
  const pageIdxInfo = getPaginationShowIdx(page, totalItems, itemsPerPage, t)

  useEffect(() => {
    let isCurrent = true
    const pagination = {
      count: itemsPerPage,
      startIndex: (page - 1) * itemsPerPage + 1
    }
    const getGroupUsers = async () => {
      await get(
        `/identity/v2alpha1/scim/v2/extensions/Groups/${groupId}/users`,
        pagination
      ).then(
        (response) => {
          if (!isCurrent) return
          if (response?.data?.Resources) {
            setGroupUsers(response?.data?.Resources)
            setTotalItems(response?.data?.totalResults)
          }
          setLoading(false)
        },
        (error) => {
          if (!isCurrent) return
          setNotificationInfo(getErrorMessage(error, t), 'error')
          setLoading(false)
        }
      )
    }
    getGroupUsers()
    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCount, page, itemsPerPage])

  const removeUserFromGroup = async () => {
    setLoading(true)
    const user = selectedUser
    const value = [
      {
        display: user?.displayName,
        $ref: `${getBaseUrl()}/identity/v2alpha1/scim/v2/Users/${user?.id}`,
        value: user?.id
      }
    ]
    try {
      await patch(`/identity/v2alpha1/scim/v2/Groups/${groupId}`, {
        schemas: [GROUP_SCHEMAS.PATCH_OP_SCHEMA],
        Operations: [
          {
            op: 'remove',
            value,
            path: 'members'
          }
        ]
      })
      setLoading(false)
      setNotificationInfo(
        <Typography type="text">
          <Trans
            i18nKey="iam:igc_users.remove_user_from_group_notif_msg_v2"
            t={t}
            values={{
              username: user?.displayName || user?.userName,
              groupName: groupDetailsData?.displayName
            }}
          />
        </Typography>,
        'info',
        t('iam:users.user_removed_title')
      )
      setGroupUsers((state) => {
        return state?.filter((item) => item?.userName !== user?.userName)
      })
      // This could affect the current user's permissions hence why we're calling updateV2RBACPolicies
      // updateV2RBACPolicies will not run on local as csrfToken will be null
      updateV2RBACPolicies(dispatchVisibilityContext, csrfToken)
      setRefreshCount((prevCount) => prevCount + 1)
    } catch (error) {
      setLoading(false)
      setNotificationInfo(getErrorMessage(error, t), 'error')
    } finally {
      setShowDeleteUsersDialog(false)
    }
  }

  const userTableColumns = [
    {
      property: 'name',
      header: t('common:name'),
      align: 'start',
      render: (datum) => {
        const label = datum?.displayName || '--'
        return isWorkspace ? (
          <Typography type="text" emphasis>
            {label}
          </Typography>
        ) : (
          <Anchor
            href="#"
            label={label}
            testId="group-user-anchor"
            onClick={(event) => {
              event?.preventDefault()
              navigate(`/manage-account/organization/users/${datum?.id}`)
            }}
          />
        )
      }
    },
    {
      property: 'userName',
      header: t('common:email'),
      align: 'start',
      primary: true
    },
    {
      property: 'verified',
      header: t('common:verified'),
      align: 'start',
      render: (datum) => {
        const verified = datum[USER_SCHEMAS?.EXTENSIONS]?.primaryEmailVerified
        return (
          <Typography testId="user-verified" type="text">
            {verified === true
              ? t('iam:verified_status_yes')
              : t('iam:verified_status_no')}
          </Typography>
        )
      }
    },
    ...(!isWorkspace && !isScim
      ? [
          {
            property: '',
            sortable: false,
            header: '',
            align: 'end',
            render: (datum) => {
              return (
                ((isAssociated && rootWkspGroupUpdatePerm) ||
                  (!isAssociated && v2GroupUpdatePerm)) && (
                  <ActionButton
                    actions={[
                      {
                        icon: <Trash />,
                        onClick: () => {
                          setSelectedUser(datum)
                          setShowDeleteUsersDialog(true)
                        }
                      }
                    ]}
                    datum={datum}
                    testId="group-user-action-btn"
                  />
                )
              )
            }
          }
        ]
      : [])
  ]

  return (
    <>
      <Box
        gap="medium"
        pad={{ top: 'medium', vertical: 'medium' }}
        width={isEmpty(groupUsers) ? 'medium' : 'large'}
      >
        <Box justify="start" gap="small">
          <Box
            direction="row"
            justify="between"
            gap="medium"
            pad={{ top: 'medium' }}
          >
            <Typography
              type="heading"
              level="2"
              testId="user-details-group-title"
            >
              {t('iam:users.users')}
            </Typography>
            {!isWorkspace &&
              !isScim &&
              !loading &&
              groupUsers?.length > 0 &&
              ((isAssociated && rootWkspGroupUpdatePerm) ||
                (!isAssociated && v2GroupUpdatePerm)) && (
                <Button
                  label={t('iam:users.add_users_label')}
                  secondary
                  testId="add-user-to-group-btn"
                  disabled={parentLoading}
                  onClick={() => {
                    setShowAddUsersDialog(true)
                  }}
                />
              )}
          </Box>
          {((groupUsers && groupUsers?.length !== 0) || loading) && (
            <Box width="large" margin={{ top: 'small' }}>
              <DataTable
                pagination={{
                  totalItems: totalItems || 0,
                  itemsPerPage,
                  setItemsPerPage,
                  rowDropDownLabel: t('auditlogs:items_per_page'),
                  defaultRowsValue: itemsPerPage,
                  page,
                  setPage,
                  pageIdxInfo,
                  rowCountOptions: [20, 50, 100, 200]
                }}
                grid={{
                  data: groupUsers,
                  columns: userTableColumns
                }}
                loading={loading || parentLoading}
                testId="user-groups-datatable"
              />
            </Box>
          )}
          {groupUsers && groupUsers?.length === 0 && (
            <Box pad={{ top: 'medium' }}>
              {isWorkspace && !isScim ? (
                <NoDataInfo
                  icon={<User size="large" />}
                  gap="small"
                  title={t('iam:users.no_users_title')}
                  testId="no-users"
                />
              ) : (
                <NoDataInfo
                  icon={<User size="large" />}
                  gap="small"
                  gapTitleSubtitle="small"
                  action={
                    ((isAssociated && rootWkspGroupUpdatePerm) ||
                      (!isAssociated && v2GroupUpdatePerm)) && (
                      <Button
                        label={t('iam:users.add_users_label')}
                        onClick={() => {
                          setShowAddUsersDialog(true)
                        }}
                        testId="add-user-to-group-btn"
                        secondary
                      />
                    )
                  }
                  title={t('iam:organization_groups.no_add_user_added')}
                  subtitle={
                    ((isAssociated && rootWkspGroupUpdatePerm) ||
                      (!isAssociated && v2GroupUpdatePerm)) &&
                    t('iam:organization_groups.get_started_add_users_to_group')
                  }
                  testId="no-assigned-user"
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {showDeleteUsersDialog && (
        <RemoveUserFromGroupConfirmation
          loading={loading}
          onSetOpen={setShowDeleteUsersDialog}
          user={selectedUser}
          userGroupToRemove={groupDetailsData}
          handleRemove={removeUserFromGroup}
        />
      )}
      {showAddUsersDialog && (
        <AssignUsersToGroupModal
          onSetOpen={setShowAddUsersDialog}
          onSubmit={() => {
            setRefreshCount((prevCount) => prevCount + 1)
          }}
          group={groupDetailsData}
          setNotificationInfo={setNotificationInfo}
        />
      )}
    </>
  )
}

GroupDetailsUsers.propTypes = {
  groupDetailsData: PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string
  }).isRequired,
  setNotificationInfo: PropTypes.func.isRequired,
  parentLoading: PropTypes.bool.isRequired,
  isAssociated: PropTypes.bool.isRequired,
  /**
   * True if called from v2 workspace, false if called from IGC pages
   */
  isWorkspace: PropTypes.bool,
  isScim: PropTypes.bool
}

export default GroupDetailsUsers
